import axios from 'axios';
import { API_BASE_URL } from './config';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Budget interface
export interface Budget {
  id: string;
  name: string;
  project: string;
  category: 'labor' | 'materials' | 'equipment' | 'overhead' | 'contingency' | 'total';
  budgetedAmount: number;
  actualAmount: number;
  committedAmount: number;
  remainingAmount: number;
  status: 'on-track' | 'over-budget' | 'under-budget' | 'at-risk';
  variance: number;
  variancePercentage: number;
  lastUpdated: string;
  createdBy: string;
  notes?: string;
  projectId?: string;
  createdAt?: string;
  updatedAt?: string;
}

// Budget filters interface
export interface BudgetFilters {
  project?: string;
  category?: string;
  status?: string;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
}

// Budget statistics interface
export interface BudgetStats {
  totalBudgeted: number;
  totalActual: number;
  totalCommitted: number;
  totalRemaining: number;
  totalVariance: number;
  variancePercentage: number;
  overBudgetCount: number;
  underBudgetCount: number;
  onTrackCount: number;
  atRiskCount: number;
  categoryBreakdown: {
    category: string;
    budgeted: number;
    actual: number;
    variance: number;
  }[];
}

// Budget creation/update interface
export interface BudgetFormData {
  name: string;
  project: string;
  category: Budget['category'];
  budgetedAmount: number;
  actualAmount?: number;
  committedAmount?: number;
  notes?: string;
  projectId?: string;
}

// Budget line item interface
export interface BudgetLineItem {
  id: string;
  budgetId: string;
  description: string;
  category: string;
  budgetedAmount: number;
  actualAmount: number;
  variance: number;
  unit?: string;
  quantity?: number;
  unitCost?: number;
}

class BudgetsService {
  private normalizeBudget(raw: any): Budget {
    const id = raw._id || raw.id || '';
    const projectObj = typeof raw.project === 'object' && raw.project !== null ? raw.project : null;
    const projectName = projectObj?.name || (typeof raw.project === 'string' ? raw.project : '') || '';
    const projectId = projectObj?._id || raw.projectId;

    const budgeted = Number(raw.totalBudget ?? raw.budgetedAmount ?? 0) || 0;
    const actual = Number(raw.totalActual ?? raw.actualAmount ?? 0) || 0;
    const committed = Number(raw.totalCommitted ?? raw.committedAmount ?? 0) || 0;
    const variance = budgeted - actual;
    const variancePercentage = budgeted > 0 ? (variance / budgeted) * 100 : 0;

    // Map backend statuses to UI statuses
    const backendStatus = raw.status || '';
    let status: Budget['status'] = 'on-track';
    if (backendStatus === 'over_budget') status = 'over-budget';
    else if (backendStatus === 'near_budget' || backendStatus === 'pending') status = 'at-risk';
    else if (backendStatus === 'rejected') status = 'at-risk';
    else if (backendStatus === 'approved') status = actual > budgeted ? 'over-budget' : 'on-track';
    else {
      // Derive status if not a known backend status
      if (actual > budgeted) status = 'over-budget';
      else if (actual < budgeted * 0.7) status = 'under-budget';
      else status = 'on-track';
    }

    // Safely derive creator name as string
    const creatorName = (() => {
      const c = raw.createdBy;
      if (!c) return '';
      if (typeof c === 'string') return c;
      const name = c.fullName || c.name || [c.firstName, c.lastName].filter(Boolean).join(' ');
      return typeof name === 'string' ? name : '';
    })();

    return {
      id,
      name: raw.name || 'Untitled Budget',
      project: projectName,
      category: (raw.category || 'total') as Budget['category'],
      budgetedAmount: budgeted,
      actualAmount: actual,
      committedAmount: committed,
      remainingAmount: Math.max(budgeted - actual, 0),
      status,
      variance,
      variancePercentage,
      lastUpdated: raw.updatedAt || raw.lastUpdated || raw.createdAt || new Date().toISOString(),
      createdBy: creatorName,
      notes: raw.notes,
      projectId,
      createdAt: raw.createdAt,
      updatedAt: raw.updatedAt,
    };
  }

  // Get all budgets
  async getBudgets(filters?: BudgetFilters): Promise<Budget[]> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
    }
    
    const response = await api.get(`/budgets?${params.toString()}`);
    const raw = response.data?.budgets ?? response.data?.data ?? (Array.isArray(response.data) ? response.data : []);
    const list: any[] = Array.isArray(raw) ? raw : [];
    return list.map((b) => this.normalizeBudget(b));
  }

  // Get budget by ID
  async getBudgetById(id: string): Promise<Budget> {
    const response = await api.get(`/budgets/${id}`);
    const raw = response.data?.budget ?? response.data?.data ?? response.data;
    return this.normalizeBudget(raw);
  }

  // Create new budget
  async createBudget(budgetData: BudgetFormData): Promise<Budget> {
    const response = await api.post('/budgets', budgetData);
    return response.data;
  }

  // Update budget
  async updateBudget(id: string, budgetData: Partial<BudgetFormData>): Promise<Budget> {
    const response = await api.put(`/budgets/${id}`, budgetData);
    return response.data;
  }

  // Delete budget
  async deleteBudget(id: string): Promise<void> {
    await api.delete(`/budgets/${id}`);
  }

  // Get budget statistics
  async getBudgetStats(filters?: BudgetFilters): Promise<BudgetStats> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
    }
    
    const response = await api.get(`/budgets/stats?${params.toString()}`);
    return response.data;
  }

  // Get budgets by project
  async getBudgetsByProject(projectId: string): Promise<Budget[]> {
    const response = await api.get(`/budgets/project/${projectId}`);
    return response.data;
  }

  // Update budget status
  async updateBudgetStatus(id: string, status: Budget['status']): Promise<Budget> {
    const response = await api.patch(`/budgets/${id}/status`, { status });
    return response.data;
  }

  // Update actual amount
  async updateActualAmount(id: string, actualAmount: number): Promise<Budget> {
    const response = await api.patch(`/budgets/${id}/actual`, { actualAmount });
    return response.data;
  }

  // Update committed amount
  async updateCommittedAmount(id: string, committedAmount: number): Promise<Budget> {
    const response = await api.patch(`/budgets/${id}/committed`, { committedAmount });
    return response.data;
  }

  // Get budget line items
  async getBudgetLineItems(budgetId: string): Promise<BudgetLineItem[]> {
    const response = await api.get(`/budgets/${budgetId}/line-items`);
    return response.data;
  }

  // Create budget line item
  async createBudgetLineItem(budgetId: string, lineItem: Omit<BudgetLineItem, 'id' | 'budgetId'>): Promise<BudgetLineItem> {
    const response = await api.post(`/budgets/${budgetId}/line-items`, lineItem);
    return response.data;
  }

  // Update budget line item
  async updateBudgetLineItem(budgetId: string, lineItemId: string, lineItem: Partial<BudgetLineItem>): Promise<BudgetLineItem> {
    const response = await api.put(`/budgets/${budgetId}/line-items/${lineItemId}`, lineItem);
    return response.data;
  }

  // Delete budget line item
  async deleteBudgetLineItem(budgetId: string, lineItemId: string): Promise<void> {
    await api.delete(`/budgets/${budgetId}/line-items/${lineItemId}`);
  }

  // Bulk update budgets
  async bulkUpdateBudgets(updates: { id: string; data: Partial<BudgetFormData> }[]): Promise<Budget[]> {
    const response = await api.patch('/budgets/bulk', { updates });
    return response.data;
  }

  // Bulk delete budgets
  async bulkDeleteBudgets(ids: string[]): Promise<void> {
    await api.delete('/budgets/bulk', { data: { ids } });
  }

  // Search budgets
  async searchBudgets(query: string, filters?: BudgetFilters): Promise<Budget[]> {
    const params = new URLSearchParams({ search: query });
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value && key !== 'search') params.append(key, value);
      });
    }
    const response = await api.get(`/budgets?${params.toString()}`);
    const raw = response.data?.budgets ?? response.data?.data ?? (Array.isArray(response.data) ? response.data : []);
    const list: any[] = Array.isArray(raw) ? raw : [];
    return list.map((b) => this.normalizeBudget(b));
  }

  // Export budgets
  async exportBudgets(format: 'csv' | 'excel' | 'pdf', filters?: BudgetFilters): Promise<Blob> {
    const params = new URLSearchParams({ format });
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
    }
    
    const response = await api.get(`/budgets/export?${params.toString()}`, {
      responseType: 'blob'
    });
    return response.data;
  }

  // Import budgets
  async importBudgets(file: File): Promise<{ success: number; errors: string[] }> {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post('/budgets/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // Get budget variance analysis
  async getBudgetVarianceAnalysis(id: string): Promise<{
    budget: Budget;
    analysis: {
      category: string;
      budgeted: number;
      actual: number;
      variance: number;
      variancePercentage: number;
      trend: 'improving' | 'declining' | 'stable';
    }[];
  }> {
    const response = await api.get(`/budgets/${id}/variance-analysis`);
    return response.data;
  }

  // Get budget forecast
  async getBudgetForecast(id: string, months: number = 6): Promise<{
    budget: Budget;
    forecast: {
      month: string;
      projectedActual: number;
      projectedVariance: number;
      confidence: number;
    }[];
  }> {
    const response = await api.get(`/budgets/${id}/forecast?months=${months}`);
    return response.data;
  }
}

const budgetsService = new BudgetsService();
export default budgetsService;